import React from 'react';
import { Smartphone, Monitor, AlertCircle, Palette, Eye } from 'lucide-react';
import DOMPurify from 'dompurify';
import type { TemplateDefinition } from '../../../types/outreach';
import BrandedHtmlFrame from '../BrandedHtmlFrame';
import LogoGrid from '../LogoGrid';
import OutreachImages from '../OutreachImages';
import DesignControls from './DesignControls';
import HelpTrigger from '../../../help/components/HelpTrigger';

const TYPE_NOUN: Record<string, string> = {
  campaign: 'Campaign',
  survey: 'Survey',
  poll: 'Poll',
  message: 'Message',
  petition: 'Petition',
};

interface Props {
  name: string;
  description?: string;
  type: string;
  definition: TemplateDefinition;
  onChange?: (definition: TemplateDefinition) => void;
  organizationLogoUrl?: string | null;
  workspaceLogoUrl?: string | null;
}

const WizardStep5Preview: React.FC<Props> = ({ name, description, type, definition, onChange, organizationLogoUrl, workspaceLogoUrl }) => {
  const [viewMode, setViewMode] = React.useState<'desktop' | 'mobile'>('desktop');
  const [activeTab, setActiveTab] = React.useState<'preview' | 'design'>('preview');

  const isMessageType = type === 'message';
  const isPetition = type === 'petition';
  const isQuestionless = isMessageType || isPetition;
  const hasQuestions = definition.questions.length > 0;
  const showSubmitButton = hasQuestions || definition.demographics.enabled;

  const warnings = [];
  if (!isQuestionless && definition.questions.length === 0) warnings.push('No questions added');
  if (!isQuestionless && !definition.demographics.enabled && definition.questions.length < 3) {
    warnings.push('Consider adding more questions or enabling demographics');
  }
  if (isPetition && !definition.demographics.enabled) {
    warnings.push('Petitions need demographics enabled — that is the signature form');
  }

  // Configurations that look fine in the builder but ship broken for respondents
  const contact = definition.legislator_contact;
  if (contact?.enabled) {
    const recipientTypes = contact.recipient_types || [];
    const districtCollected = [
      ...(definition.demographics.required_fields || []),
      ...(definition.demographics.optional_fields || [])
    ].includes('district');

    if (recipientTypes.length === 0) {
      warnings.push('One-click contact is on but no recipients are selected — the contact card will reach nobody');
    }
    if (!districtCollected && recipientTypes.some(rt => rt === 'house_rep' || rt === 'senate_rep')) {
      warnings.push('House/Senate recipients need District Lookup enabled in Demographics, or they resolve to nobody');
    }
    if (recipientTypes.includes('custom') && (contact.custom_recipients || []).length === 0) {
      warnings.push('Custom Distribution is on but no recipients have been added');
    }
  }
  if (definition.share_results_link) {
    warnings.push('Share results link is on — respondents only get a working link if a report is marked "Set as Public Results View" on the Reports & Communications tab');
  }

  // Load Google Fonts dynamically
  React.useEffect(() => {
    const headingFont = definition.branding?.fonts?.heading || 'Inter';
    const bodyFont = definition.branding?.fonts?.body || 'Inter';
    const fonts = Array.from(new Set([headingFont, bodyFont]));
    
    // Remove any existing dynamic font links
    const existingLinks = document.querySelectorAll('link[data-wizard-font]');
    existingLinks.forEach(link => link.remove());
    
    // Add new font link
    const fontFamilies = fonts.map(font => font.replace(' ', '+')).join('&family=');
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${fontFamilies}:wght@400;500;600;700&display=swap`;
    link.setAttribute('data-wizard-font', 'true');
    document.head.appendChild(link);
    
    return () => {
      const links = document.querySelectorAll('link[data-wizard-font]');
      links.forEach(link => link.remove());
    };
  }, [definition.branding?.fonts?.heading, definition.branding?.fonts?.body]);

  // Get layout-specific styles
  const getLayoutStyles = () => {
    const template = definition.branding?.layout_template || 'modern';
    switch (template) {
      case 'modern':
        return {
          containerClass: 'rounded-lg shadow-lg',
          headerClass: 'py-6 px-8 border-b',
          contentClass: 'p-8',
          footerClass: 'py-6 px-8 border-t'
        };
      case 'corporate':
        return {
          containerClass: 'rounded-none shadow-2xl',
          headerClass: 'py-8 px-12 border-b-4',
          contentClass: 'p-12',
          footerClass: 'py-8 px-12 border-t-4'
        };
      case 'minimal':
        return {
          containerClass: 'rounded-sm shadow-sm',
          headerClass: 'py-4 px-6',
          contentClass: 'p-6',
          footerClass: 'py-4 px-6'
        };
      case 'branded':
        return {
          containerClass: 'rounded-xl shadow-2xl border-4',
          headerClass: 'py-8 px-10 bg-gradient-to-r',
          contentClass: 'p-10',
          footerClass: 'py-8 px-10 bg-gradient-to-r'
        };
      default:
        return {
          containerClass: 'rounded-lg shadow-lg',
          headerClass: 'py-6 px-8 border-b',
          contentClass: 'p-8',
          footerClass: 'py-6 px-8 border-t'
        };
    }
  };

  const layoutStyles = getLayoutStyles();

  if (activeTab === 'design' && onChange) {
    return (
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Tab Toggle */}
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setActiveTab('design')}
            className="px-4 py-2 rounded-lg flex items-center gap-2 bg-primary-600 text-white"
          >
            <Palette className="w-4 h-4" />
            Design
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className="px-4 py-2 rounded-lg flex items-center gap-2 bg-background-tertiary dark:bg-background-dark-tertiary text-text-secondary"
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
        </div>

        <DesignControls
          definition={definition}
          onChange={onChange}
          organizationLogoUrl={organizationLogoUrl}
          workspaceLogoUrl={workspaceLogoUrl}
        />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-2">
          Preview Your {TYPE_NOUN[type] || 'Outreach'}
        </h2>
        <p className="text-text-secondary dark:text-text-dark-secondary">
          This is how respondents will see your outreach
        </p>
      </div>

      {/* Tabs (what you're looking at) and, within Preview, the viewport toggle */}
      <div className="flex flex-col items-center gap-3">
        <div className="flex justify-center gap-2">
          {onChange && (
            <button
              onClick={() => setActiveTab('design')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                activeTab === 'design'
                  ? 'bg-primary-600 text-white'
                  : 'bg-background-tertiary dark:bg-background-dark-tertiary text-text-secondary'
              }`}
            >
              <Palette className="w-4 h-4" />
              Design
            </button>
          )}
          <button
            onClick={() => setActiveTab('preview')}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
              activeTab === 'preview'
                ? 'bg-primary-600 text-white'
                : 'bg-background-tertiary dark:bg-background-dark-tertiary text-text-secondary'
            }`}
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
          <HelpTrigger contentKey="outreach-design" size="sm" />
        </div>

        {activeTab === 'preview' && (
          <div className="flex justify-center gap-2">
            <button
              onClick={() => setViewMode('desktop')}
              className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-2 ${
                viewMode === 'desktop'
                  ? 'bg-background-tertiary dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary'
                  : 'bg-background-tertiary dark:bg-background-dark-tertiary text-text-secondary'
              }`}
            >
              <Monitor className="w-4 h-4" />
              Desktop
            </button>
            <button
              onClick={() => setViewMode('mobile')}
              className={`px-3 py-1.5 text-sm rounded-lg flex items-center gap-2 ${
                viewMode === 'mobile'
                  ? 'bg-background-tertiary dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary'
                  : 'bg-background-tertiary dark:bg-background-dark-tertiary text-text-secondary'
              }`}
            >
              <Smartphone className="w-4 h-4" />
              Mobile
            </button>
          </div>
        )}
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="card bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-amber-900 dark:text-amber-200 mb-1">
                Suggestions
              </div>
              <ul className="text-sm text-amber-800 dark:text-amber-300 space-y-1">
                {warnings.map((warning, i) => (
                  <li key={i}>• {warning}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Preview */}
      <div className={`mx-auto transition-all ${viewMode === 'mobile' ? 'max-w-sm' : 'max-w-3xl'}`}>
        <div 
          className={`${layoutStyles.containerClass} overflow-hidden`}
          style={{
            backgroundColor: definition.branding?.colors?.background || '#ffffff',
            color: definition.branding?.colors?.text || '#1e293b',
            fontFamily: definition.branding?.fonts?.body || 'Inter, sans-serif',
            borderColor: definition.branding?.layout_template === 'branded' ? definition.branding?.colors?.primary : undefined
          }}
        >
          {/* Header with Logo */}
          <div 
            className={`relative ${layoutStyles.headerClass}`}
            style={{ 
              borderColor: definition.branding?.colors?.secondary || '#e2e8f0',
              backgroundImage: definition.branding?.layout_template === 'branded' ? 
                `linear-gradient(to right, ${definition.branding?.colors?.primary}15, ${definition.branding?.colors?.secondary}15)` : undefined
            }}
          >
            <LogoGrid logos={definition.branding?.logos} area="header" />
          </div>

          {/* Custom Header HTML */}
          {definition.branding?.custom_header_html && (
            <BrandedHtmlFrame html={definition.branding.custom_header_html} />
          )}

          {/* Content */}
          <div className={layoutStyles.contentClass}>
            <h1 
              className="text-3xl font-bold mb-2"
              style={{
                fontFamily: definition.branding?.fonts?.heading || 'Inter, sans-serif',
                color: definition.branding?.colors?.primary || '#2563eb'
              }}
            >
              {name || 'Untitled Outreach'}
            </h1>
            <div className="text-sm mb-4" style={{ color: definition.branding?.colors?.secondary || '#64748b' }}>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </div>

            {/* Images — same position as the public page (between title and description).
                Staff-only preview, so the serve-proxy URL derived from the key is fine. */}
            <OutreachImages
              images={definition.images?.map(img => ({ url: `/api/files/serve/${img.key}`, alt: img.alt }))}
            />

            {/* Description */}
            {description && (
              <div 
                className="mt-4 mb-6 prose prose-sm max-w-none"
                style={{ color: definition.branding?.colors?.text || '#1e293b' }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(description) }}
              />
            )}

            {/* Entity Links */}
            {definition.entity_links && definition.entity_links.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {definition.entity_links.map((link: any, i: number) => (
                  <div
                    key={i}
                    className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm"
                    style={{ 
                      backgroundColor: `${definition.branding?.colors?.primary || '#2563eb'}15`,
                      color: definition.branding?.colors?.primary || '#2563eb'
                    }}
                  >
                    {link.entity_display_name}
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-6">
              {hasQuestions && definition.questions.map((q, i) => (
                <div key={q.id} className="space-y-2">
                  <label className="block font-medium">
                    {i + 1}. {q.question}
                    {q.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  
                  {/* Single Select */}
                  {q.type === 'single_select' && q.options && (
                    <div className="space-y-2">
                      {q.options.map((opt) => (
                        <div key={opt.value} className="flex items-center gap-2 p-3 rounded-lg border" style={{ borderColor: definition.branding?.colors?.secondary + '40' }}>
                          <div className="w-5 h-5 rounded-full border-2 flex-shrink-0" style={{ borderColor: definition.branding?.colors?.secondary }} />
                          <span style={{ color: definition.branding?.colors?.text }}>{opt.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Multi Select */}
                  {q.type === 'multi_select' && q.options && (
                    <div className="space-y-2">
                      {q.options.map((opt) => (
                        <div key={opt.value} className="flex items-center gap-2 p-3 rounded-lg border" style={{ borderColor: definition.branding?.colors?.secondary + '40' }}>
                          <div className="w-5 h-5 rounded border-2 flex-shrink-0" style={{ borderColor: definition.branding?.colors?.secondary }} />
                          <span style={{ color: definition.branding?.colors?.text }}>{opt.label}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Long Text */}
                  {q.type === 'long_text' && (
                    <textarea
                      disabled
                      placeholder={q.placeholder || 'Type answer here...'}
                      className="w-full min-h-[100px] px-4 py-3 rounded-lg border"
                      style={{
                        backgroundColor: definition.branding?.colors?.background || '#ffffff',
                        borderColor: definition.branding?.colors?.secondary,
                        color: definition.branding?.colors?.text
                      }}
                    />
                  )}
                  
                  {/* Short Text & Email */}
                  {(q.type === 'short_text' || q.type === 'email') && (
                    <input
                      type={q.type === 'email' ? 'email' : 'text'}
                      disabled
                      placeholder={q.placeholder || 'Type answer here...'}
                      className="w-full px-4 py-3 rounded-lg border"
                      style={{
                        backgroundColor: definition.branding?.colors?.background || '#ffffff',
                        borderColor: definition.branding?.colors?.secondary,
                        color: definition.branding?.colors?.text
                      }}
                    />
                  )}
                  
                  {/* NPS */}
                  {q.type === 'nps' && (
                    <div className="flex justify-between gap-1">
                      {Array.from({ length: 11 }, (_, i) => (
                        <div
                          key={i}
                          className="w-10 h-10 rounded-lg flex items-center justify-center font-medium border"
                          style={{
                            borderColor: definition.branding?.colors?.secondary,
                            color: definition.branding?.colors?.text
                          }}
                        >
                          {i}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Numeric */}
                  {q.type === 'numeric' && (
                    <input
                      type="number"
                      disabled
                      className="w-full px-4 py-3 rounded-lg border"
                      style={{
                        backgroundColor: definition.branding?.colors?.background || '#ffffff',
                        borderColor: definition.branding?.colors?.secondary,
                        color: definition.branding?.colors?.text
                      }}
                    />
                  )}
                  
                  {/* Date */}
                  {q.type === 'date' && (
                    <input
                      type="date"
                      disabled
                      className="w-full px-4 py-3 rounded-lg border"
                      style={{
                        backgroundColor: definition.branding?.colors?.background || '#ffffff',
                        borderColor: definition.branding?.colors?.secondary,
                        color: definition.branding?.colors?.text
                      }}
                    />
                  )}
                </div>
              ))}

              {definition.demographics.enabled && (
                <div className="pt-6 border-t border-border-light dark:border-border-dark">
                  <h3 
                    className="font-semibold mb-4"
                    style={{ 
                      fontFamily: definition.branding?.fonts?.heading || 'Inter',
                      color: definition.branding?.colors?.text
                    }}
                  >
                    About You
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[...definition.demographics.required_fields, ...definition.demographics.optional_fields].map((field) => {
                      const isRequired = definition.demographics.required_fields.includes(field);
                      const label = field.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                      
                      // Special handling for district field
                      if (field === 'district') {
                        return (
                          <div key={field} className="md:col-span-2">
                            <label 
                              className="block text-sm font-medium mb-1"
                              style={{ color: definition.branding?.colors?.secondary }}
                            >
                              {label}{isRequired && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            <input
                              type="text"
                              disabled
                              placeholder="Enter your address..."
                              className="w-full px-4 py-2 rounded-lg border"
                              style={{
                                backgroundColor: definition.branding?.colors?.background || '#ffffff',
                                borderColor: definition.branding?.colors?.secondary
                              }}
                            />
                          </div>
                        );
                      }

                      if (field === 'age_range' || field === 'party_affiliation' || field === 'gender') {
                        const placeholder = field === 'age_range' ? 'Select age range'
                          : field === 'party_affiliation' ? 'Select party affiliation'
                          : 'Select gender';
                        return (
                          <div key={field}>
                            <label
                              className="block text-sm font-medium mb-1"
                              style={{ color: definition.branding?.colors?.secondary }}
                            >
                              {label}{isRequired && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            <select
                              disabled
                              className="w-full px-4 py-2 rounded-lg border"
                              style={{
                                backgroundColor: definition.branding?.colors?.background || '#ffffff',
                                borderColor: definition.branding?.colors?.secondary
                              }}
                            >
                              <option>{placeholder}</option>
                            </select>
                          </div>
                        );
                      }
                      
                      return (
                        <div key={field}>
                          <label 
                            className="block text-sm font-medium mb-1"
                            style={{ color: definition.branding?.colors?.secondary }}
                          >
                            {label}{isRequired && <span className="text-red-500 ml-1">*</span>}
                          </label>
                          <input
                            type={field === 'email' ? 'email' : 'text'}
                            disabled
                            className="w-full px-4 py-2 rounded-lg border"
                            style={{
                              backgroundColor: definition.branding?.colors?.background || '#ffffff',
                              borderColor: definition.branding?.colors?.secondary
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {showSubmitButton && (
                <button
                  disabled
                  className="w-full py-3 rounded-lg font-semibold"
                  style={{
                    backgroundColor: definition.branding?.colors?.primary || '#2563eb',
                    color: '#ffffff'
                  }}
                >
                  {isPetition ? 'Sign the Petition' : 'Submit Response'}
                </button>
              )}

              {isMessageType && !showSubmitButton && definition.legislator_contact?.enabled && (
                <div
                  className="p-4 rounded-lg border text-center"
                  style={{ borderColor: definition.branding?.colors?.primary || '#2563eb' }}
                >
                  <p className="text-sm font-medium" style={{ color: definition.branding?.colors?.primary || '#2563eb' }}>
                    Legislator Contact Card will appear here
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer with Logo */}
          <div 
            className={layoutStyles.footerClass}
            style={{ 
              borderColor: definition.branding?.colors?.secondary || '#e2e8f0',
              backgroundImage: definition.branding?.layout_template === 'branded' ? 
                `linear-gradient(to right, ${definition.branding?.colors?.secondary}15, ${definition.branding?.colors?.primary}15)` : undefined
            }}
          >
            <LogoGrid logos={definition.branding?.logos} area="footer" className="mb-4" />

            {/* Custom Footer HTML */}
            {definition.branding?.custom_footer_html && (
              <BrandedHtmlFrame html={definition.branding.custom_footer_html} />
            )}

            {/* Powered By */}
            <div className="text-center text-xs" style={{ color: definition.branding?.colors?.secondary || '#94a3b8' }}>
              Powered by <span className="font-semibold">Capitol Commons PBC</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WizardStep5Preview;

